import SearchArea from "component/admin/search/searchArea";
import style from "css/admin/component.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Button, ConfigProvider, Table, message } from "antd";
import { UploadOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import SearchButton from "component/admin/search/searchButton";
import SearchDropdown from "component/admin/search/searchDropdown";
import SearchInput from "component/admin/search/searchInput";
import SearchItem from "component/admin/search/searchItem";
import SearchRow from "component/admin/search/searchRow";
import MemberUploadModal from "./memberUploadModal";
import MemberManualRegisterModal from "./memberManualRegisterModal";
import MemberDeleteConfirmModal from "./memberDeleteConfirmModal";

function User() {
  const [init, setInit] = useState(true);

  // 검색 필터
  const [searchCampus, setSearchCampus] = useState("ALL");
  const [searchDepartment, setSearchDepartment] = useState("ALL");
  const [searchType, setSearchType] = useState("ALL");
  const [searchStatus, setSearchStatus] = useState("ALL");
  const [searchText, setSearchText] = useState("");

  // 캠퍼스 옵션
  const [campusList, setCampusList] = useState([]);
  const [campusLoading, setCampusLoading] = useState(false);

  // Select box 옵션
  const [departmentList, setDepartmentList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [statusList, setStatusList] = useState([]);

  // Select box 옵션 로딩
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [typeLoading, setTypeLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // 조회된 결과 리스트
  const [dataList, setDataList] = useState([]);
  const [totalCnt, setTotalCnt] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataLoading, setDataLoading] = useState(false);
  const pageSize = 10;

  // 선택된 데이터 리스트
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // 인사정보 업로드 모달
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // 인사정보 수기등록 모달
  const [manualModalOpen, setManualModalOpen] = useState(false);

  // 삭제 확인 모달
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 데이터 조회
  const getMemberList = useCallback(
    (page = 1) => {
      setDataLoading(true);
      const config = {
        params: {
          CAMPUS_CD: searchCampus,
          DEPARTMENT_CD: searchDepartment,
          MEMBER_TYPE: searchType,
          STATUS: searchStatus,
          keyword: searchText,
          page: page,
          size: pageSize,
        },
      };
      axios
        .get("/user/list", config)
        .then((res) => {
          if (res.status === 200) {
            setDataList(res.data.list || []);
            setTotalCnt(res.data.totalCnt || 0);
            setCurrentPage(res.data.page || 1);
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => setDataLoading(false));
    },
    [searchCampus, searchDepartment, searchType, searchStatus, searchText],
  );

  // 구분 코드 → 라벨 변환
  const getTypeMeta = useCallback(
    (typeCode) => {
      if (typeCode == null) return { label: "-", code: "", matched: true };
      const code = String(typeCode).trim();
      if (!code) return { label: "-", code: "", matched: true };

      const matched = typeList.find((it) => String(it?.key) === code);
      if (matched?.label) return { label: matched.label, code, matched: true };

      return { label: code, code, matched: false };
    },
    [typeList],
  );

  // 재학여부 코드 → 라벨 변환
  const getStatusMeta = useCallback(
    (statusCode) => {
      if (statusCode == null) return { label: "-", code: "", matched: true };
      const code = String(statusCode).trim();
      if (!code) return { label: "-", code: "", matched: true };

      const matched = statusList.find((it) => String(it?.key) === code);
      if (matched?.label) return { label: matched.label, code, matched: true };

      return { label: code, code, matched: false };
    },
    [statusList],
  );

  // 캠퍼스 dropdown 옵션 조회 (CD06: 캠퍼스)
  const getCampusList = useCallback(() => {
    setCampusLoading(true);
    const config = {
      params: {
        GROUP_CD: "CD06",
      },
    };
    axios
      .get("/code", config)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const items = [
            { key: "ALL", label: "전체" },
            ...res.data.map((row) => ({
              key: row?.CODE_CD,
              label: row?.VIEW_NM,
            })),
          ];
          setCampusList(items);
        } else {
          setCampusList([{ key: "ALL", label: "전체" }]);
        }
      })
      .catch((err) => {
        console.log(err);
        setCampusList([{ key: "ALL", label: "전체" }]);
      })
      .finally(() => setCampusLoading(false));
  }, []);

  // 학과 dropdown 옵션 조회
  const getDepartmentList = useCallback(() => {
    setDepartmentLoading(true);
    axios
      .get("/department")
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const items = [
            { key: "ALL", label: "전체" },
            ...res.data.map((row) => ({
              key: String(row?.DEPARTMENT_CD),
              label: row?.DEPARTMENT_NM,
            })),
          ];
          setDepartmentList(items);
        } else {
          setDepartmentList([{ key: "ALL", label: "전체" }]);
        }
      })
      .catch((err) => {
        console.log(err);
        setDepartmentList([{ key: "ALL", label: "전체" }]);
      })
      .finally(() => setDepartmentLoading(false));
  }, []);

  // 구분 dropdown 옵션 조회 (CD01: 신분)
  const getTypeList = useCallback(() => {
    setTypeLoading(true);
    const config = {
      params: {
        GROUP_CD: "CD01",
      },
    };
    axios
      .get("/code", config)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const items = [
            { key: "ALL", label: "전체" },
            ...res.data.map((row) => ({
              key: row?.CODE_CD,
              label: row?.VIEW_NM,
            })),
          ];
          setTypeList(items);
        } else {
          setTypeList([{ key: "ALL", label: "전체" }]);
        }
      })
      .catch((err) => {
        console.log(err);
        setTypeList([{ key: "ALL", label: "전체" }]);
      })
      .finally(() => setTypeLoading(false));
  }, []);

  // 재학여부 dropdown 옵션 조회 (CD04: 재학/재직, CD05: 퇴학/퇴직)
  const getStatusList = useCallback(() => {
    setStatusLoading(true);
    Promise.all([
      axios.get("/code", { params: { GROUP_CD: "CD04" } }),
      axios.get("/code", { params: { GROUP_CD: "CD05" } }),
    ])
      .then(([res04, res05]) => {
        const list04 =
          res04.status === 200 && Array.isArray(res04.data) ? res04.data : [];
        const list05 =
          res05.status === 200 && Array.isArray(res05.data) ? res05.data : [];
        const items = [
          { key: "ALL", label: "전체" },
          ...[...list04, ...list05].map((row) => ({
            key: row?.CODE_CD,
            label: row?.VIEW_NM,
          })),
        ];
        setStatusList(items);
      })
      .catch((err) => {
        console.log(err);
        setStatusList([{ key: "ALL", label: "전체" }]);
      })
      .finally(() => setStatusLoading(false));
  }, []);

  useEffect(() => {
    if (init) {
      getCampusList();
      getDepartmentList();
      getTypeList();
      getStatusList();
      getMemberList(1);
      setInit(false);
    }
  }, [getCampusList, getDepartmentList, getTypeList, getStatusList, getMemberList, init]);

  const columns = useMemo(
    () => [
      {
        title: "학번",
        dataIndex: "MEMBER_ID",
        key: "MEMBER_ID",
        width: 120,
        render: (text) => <span className={style.table_cell}>{text}</span>,
      },
      {
        title: "이름",
        dataIndex: "MEMBER_NM",
        key: "MEMBER_NM",
        width: 100,
        render: (text) => <span className={style.table_cell}>{text}</span>,
      },
      {
        title: "이메일",
        dataIndex: "EMAIL",
        key: "EMAIL",
        width: 220,
        render: (text) => (
          <span className={style.table_cell}>{text || "-"}</span>
        ),
      },
      {
        title: "캠퍼스",
        dataIndex: "CAMPUS",
        key: "CAMPUS",
        width: 120,
        render: (text) => (
          <span className={style.table_cell}>{text || "-"}</span>
        ),
      },
      {
        title: "학과",
        dataIndex: "DEPARTMENT_NM",
        key: "DEPARTMENT_NM",
        width: 160,
        render: (text) => (
          <span className={style.table_cell}>{text || "-"}</span>
        ),
      },
      {
        title: "구분",
        dataIndex: "MEMBER_TYPE",
        key: "MEMBER_TYPE",
        width: 100,
        render: (code) => {
          const meta = getTypeMeta(code);
          const labelNode = meta.matched ? (
            meta.label
          ) : (
            <span title={`미매칭 코드: ${meta.code}`}>{meta.label}</span>
          );
          return <span className={style.table_cell}>{labelNode}</span>;
        },
      },
      {
        title: "재학여부",
        dataIndex: "STATUS",
        key: "STATUS",
        width: 100,
        render: (code) => {
          const meta = getStatusMeta(code);
          const labelNode = meta.matched ? (
            meta.label
          ) : (
            <span title={`미매칭 코드: ${meta.code}`}>{meta.label}</span>
          );
          return <span className={style.table_cell}>{labelNode}</span>;
        },
      },
    ],
    [getTypeMeta, getStatusMeta],
  );

  // 삭제 대상 레코드
  const deleteTargetRecords = useMemo(
    () => dataList.filter((r) => selectedRowKeys.includes(r.MEMBER_ID)),
    [dataList, selectedRowKeys],
  );

  // 삭제 버튼 클릭
  const handleDeleteClick = useCallback(() => {
    if (selectedRowKeys.length === 0) {
      message.warning("삭제할 사용자를 선택하세요.");
      return;
    }
    setDeleteModalOpen(true);
  }, [selectedRowKeys]);

  // 삭제 확인
  const handleDeleteConfirm = useCallback(async () => {
    setDeleteLoading(true);
    let successCnt = 0;
    let failCnt = 0;
    for (const memberId of selectedRowKeys) {
      try {
        await axios.post("/user/delete", { MEMBER_ID: memberId });
        successCnt++;
      } catch (e) {
        failCnt++;
      }
    }
    setDeleteLoading(false);
    setDeleteModalOpen(false);
    setSelectedRowKeys([]);
    if (failCnt === 0) {
      message.success(`${successCnt}명 삭제 완료`);
    } else {
      message.warning(`${successCnt}명 삭제, ${failCnt}명 실패`);
    }
    getMemberList(currentPage);
  }, [selectedRowKeys, getMemberList, currentPage]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys) => {
        setSelectedRowKeys(keys);
      },
    }),
    [selectedRowKeys],
  );

  return (
    <div className={style.page_wrap}>
      <SearchArea title="사용자 관리">
        <div className={style.searchItemArea}>
          <SearchRow>
            <SearchItem label="캠퍼스">
              <SearchDropdown
                items={campusList}
                selectedKey={searchCampus}
                loading={campusLoading}
                onChange={(key, label) => {
                  setSearchCampus(key);
                }}
              />
            </SearchItem>
            <SearchItem label="학과">
              <SearchDropdown
                items={departmentList}
                selectedKey={searchDepartment}
                loading={departmentLoading}
                onChange={(key, label) => {
                  setSearchDepartment(key);
                }}
              />
            </SearchItem>
            <SearchItem label="구분">
              <SearchDropdown
                items={typeList}
                selectedKey={searchType}
                loading={typeLoading}
                onChange={(key, label) => {
                  setSearchType(key);
                }}
              />
            </SearchItem>
            <SearchItem label="재학여부">
              <SearchDropdown
                items={statusList}
                selectedKey={searchStatus}
                loading={statusLoading}
                onChange={(key, label) => {
                  setSearchStatus(key);
                }}
              />
            </SearchItem>
          </SearchRow>

          <SearchRow>
            <SearchItem>
              <SearchInput
                placeholder="학번, 이름, 이메일 검색"
                width="100%"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setCurrentPage(1);
                    setSelectedRowKeys([]);
                    getMemberList(1);
                  }
                }}
              />
            </SearchItem>
            <SearchItem>
              <SearchButton
                onClick={() => {
                  setCurrentPage(1);
                  setSelectedRowKeys([]);
                  getMemberList(1);
                }}
                disabled={dataLoading}
              >
                조회
              </SearchButton>
            </SearchItem>
          </SearchRow>
        </div>
      </SearchArea>

      <div className={style.content_wrapper}>
        <div className={style.content_container}>
          <div className={style.header}>
            <div className={style.text}>전체 사용자 목록 (총 {totalCnt}명)</div>
            <ConfigProvider
              theme={{
                token: { colorPrimary: "#527ff2" },
                components: {
                  Button: {
                    colorPrimaryHover: "#4a72de",
                    colorPrimaryActive: "#3f66c8",
                  },
                },
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDeleteClick}
                  disabled={selectedRowKeys.length === 0}
                >
                  삭제
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setManualModalOpen(true)}
                >
                  수기등록
                </Button>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModalOpen(true)}
                >
                  인사정보 업로드
                </Button>
              </div>
            </ConfigProvider>
          </div>
          <div className={style.content}>
            <div className={style.table_section}>
              <Table
                rowKey="MEMBER_ID"
                rowSelection={{ ...rowSelection }}
                columns={columns}
                dataSource={dataList}
                loading={dataLoading}
                size="middle"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCnt,
                  showSizeChanger: false,
                  onChange: (page) => {
                    setCurrentPage(page);
                    setSelectedRowKeys([]);
                    getMemberList(page);
                  },
                }}
                scroll={{ x: 800 }}
              />
            </div>
          </div>
        </div>
      </div>

      <MemberUploadModal
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        onSuccess={() => {
          setUploadModalOpen(false);
          getMemberList(1);
        }}
      />

      <MemberManualRegisterModal
        open={manualModalOpen}
        onCancel={() => setManualModalOpen(false)}
        onSuccess={() => {
          setManualModalOpen(false);
          getDepartmentList();
          getMemberList(1);
        }}
      />

      <MemberDeleteConfirmModal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        records={deleteTargetRecords}
      />

    </div>
  );
}

export default User;
